import { useState, useEffect, useMemo } from 'react';
import {
  Zap,
  Plus,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Eye,
  Database,
} from 'lucide-react';
import { useMappingStore } from '../../../stores/mappingStore';
import { useStudioStore } from '../../../stores/studioStore';
import { getCompatibleMappableProperties } from '../../../lib/elementDynamic';
import type {
  SourceModule,
  EnhancedFieldMapping,
  DiscoveredVirtualField,
} from '../../../types/mapping';
import SchemaExplorer from './SchemaExplorer';
import MappingCard from './MappingCard';
import MappingForm from './MappingForm';
import DataPreviewPanel from './DataPreviewPanel';

interface DynamicFieldPanelProps {
  elementId: string;
  elementType: 'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line';
  contentType: 'STATIC' | 'DYNAMIC';
  elementIds?: string[];
  elementTypes?: Array<'text' | 'image' | 'shape' | 'button' | 'path' | 'svg' | 'line' | 'group'>;
}

type PanelView = 'mappings' | 'explorer' | 'preview';

export default function DynamicFieldPanel({
  elementId,
  elementType,
  contentType,
  elementIds,
  elementTypes,
}: DynamicFieldPanelProps) {
  const { elementMappings, validateMapping, schema, loadSchema } = useMappingStore();
  const { updateElement } = useStudioStore();

  const [activeView, setActiveView] = useState<PanelView>('mappings');
  const [showForm, setShowForm] = useState(false);
  const [editingMapping, setEditingMapping] = useState<EnhancedFieldMapping | null>(null);

  const [preSelectedSource, setPreSelectedSource] = useState<SourceModule | null>(null);
  const [preSelectedTable, setPreSelectedTable] = useState<string | null>(null);
  const [preSelectedColumn, setPreSelectedColumn] = useState<string | null>(null);
  const [preSelectedDataType, setPreSelectedDataType] = useState<string | null>(null);
  const [preSelectedVirtualField, setPreSelectedVirtualField] =
    useState<DiscoveredVirtualField | null>(null);

  const [multiElementDraft, setMultiElementDraft] = useState<{
    elementIds: string[];
    validProperties: string[];
  } | null>(null);

  const isMultiMode = Array.isArray(elementIds) && elementIds.length > 1 && Array.isArray(elementTypes);
  const compatibleProperties = isMultiMode
    ? getCompatibleMappableProperties(elementTypes!)
    : null;

  const mappings = elementMappings[elementId] ?? [];

  const validationSummary = useMemo(() => {
    let validCount = 0;
    let warningCount = 0;
    for (const m of mappings) {
      const v = validateMapping(m, elementType);
      if (v.isValid) validCount++;
      warningCount += v.warnings.length;
    }
    return {
      totalCount: mappings.length,
      validCount,
      warningCount,
      allValid: validCount === mappings.length,
    };
  }, [mappings, elementType, validateMapping]);

  useEffect(() => {
    if (!schema) {
      loadSchema();
    }
  }, [schema, loadSchema]);

  const handleColumnSelect = (
    source: SourceModule,
    tableName: string,
    columnName: string,
    dataType: string,
    virtualField?: DiscoveredVirtualField | null
  ) => {
    setPreSelectedSource(source);
    setPreSelectedTable(tableName);
    setPreSelectedColumn(columnName);
    setPreSelectedDataType(dataType);
    setPreSelectedVirtualField(virtualField ?? null);
    setEditingMapping(null);
    setShowForm(true);
    setActiveView('mappings');
  };

  const handleEdit = (mapping: EnhancedFieldMapping) => {
    setEditingMapping(mapping);
    setPreSelectedSource(null);
    setPreSelectedTable(null);
    setPreSelectedColumn(null);
    setPreSelectedDataType(null);
    setPreSelectedVirtualField(null);
    setShowForm(true);
  };

  const handleNewMapping = () => {
    setEditingMapping(null);
    setPreSelectedSource(null);
    setPreSelectedTable(null);
    setPreSelectedColumn(null);
    setPreSelectedDataType(null);
    setPreSelectedVirtualField(null);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingMapping(null);
    setMultiElementDraft(null);
    setPreSelectedSource(null);
    setPreSelectedTable(null);
    setPreSelectedColumn(null);
    setPreSelectedDataType(null);
    setPreSelectedVirtualField(null);
  };

  const handleFormSaved = () => {
    setShowForm(false);
    setEditingMapping(null);
    setMultiElementDraft(null);
    setPreSelectedVirtualField(null);
  };

  const handleNewMappingMulti = () => {
    if (!isMultiMode || !compatibleProperties) return;
    setMultiElementDraft({
      elementIds: elementIds!,
      validProperties: compatibleProperties,
    });
    setEditingMapping(null);
    setPreSelectedSource(null);
    setPreSelectedTable(null);
    setPreSelectedColumn(null);
    setPreSelectedDataType(null);
    setPreSelectedVirtualField(null);
    setShowForm(true);
  };

  if (isMultiMode) {
    return (
      <div className="space-y-3">
        <div className="py-2">
          <h4 className="text-[10px] font-semibold text-cyan-300 uppercase tracking-wider">
            Collection Mapping
          </h4>
          <p className="text-[10px] text-zinc-500 mt-1 leading-relaxed">
            Apply one mapping to all {elementIds!.length} selected elements.
          </p>
        </div>

        {compatibleProperties && compatibleProperties.length === 0 ? (
          <div className="py-2">
            <p className="text-[10px] text-amber-400 leading-relaxed">
              No mappable properties are common across the selected element types.
              Select elements of the same type or with overlapping properties.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[10px] text-zinc-500 leading-relaxed">
              Compatible properties: {compatibleProperties!.join(', ')}
            </p>

            {showForm && (
              <MappingForm
                elementId={elementId}
                elementType={elementType}
                editingMapping={null}
                preSelectedSource={preSelectedSource}
                preSelectedTable={preSelectedTable}
                preSelectedColumn={preSelectedColumn}
                preSelectedDataType={preSelectedDataType}
                preSelectedVirtualField={preSelectedVirtualField}
                onClose={() => {
                  setShowForm(false);
                  setMultiElementDraft(null);
                  setPreSelectedVirtualField(null);
                }}
                onSaved={handleFormSaved}
                multiElementIds={multiElementDraft?.elementIds}
                multiValidProperties={multiElementDraft?.validProperties}
              />
            )}

            {!showForm && (
              <button
                onClick={handleNewMappingMulti}
                className="w-full px-3 py-2 text-[11px] font-medium text-cyan-200 bg-cyan-500/10 hover:bg-cyan-500/20 border border-cyan-500/30 rounded-lg transition-colors"
              >
                <Plus className="w-3.5 h-3.5 inline mr-1.5" />
                Add Collection Mapping
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  if (contentType === 'STATIC') {
    return (
      <div className="p-3">
        <div className="text-center py-4">
          <Zap className="w-6 h-6 text-white/15 mx-auto mb-2" />
          <p className="text-xs text-white/40 mb-3">
            Dynamic field mapping requires the element to be set to{' '}
            <span className="text-purple-300 font-medium">Dynamic</span> content type.
          </p>
          <button
            onClick={() => updateElement(elementId, { contentType: 'DYNAMIC' })}
            className="px-3 py-1.5 text-xs font-medium rounded-md bg-purple-500/20 border border-purple-500/30 text-purple-300 hover:bg-purple-500/30 transition-colors"
          >
            Switch to Dynamic
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {mappings.length > 0 && (
        <div
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md border ${
            validationSummary.allValid
              ? 'bg-emerald-500/5 border-emerald-500/15'
              : 'bg-red-500/5 border-red-500/15'
          }`}
        >
          {validationSummary.allValid ? (
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
          ) : (
            <XCircle className="w-3.5 h-3.5 text-red-400" />
          )}
          <span
            className={`text-[10px] font-medium ${
              validationSummary.allValid ? 'text-emerald-400' : 'text-red-400'
            }`}
          >
            {validationSummary.validCount}/{validationSummary.totalCount} mappings valid
          </span>
          {validationSummary.warningCount > 0 && (
            <span className="flex items-center gap-1 text-[10px] text-amber-400 ml-auto">
              <AlertTriangle className="w-3 h-3" />
              {validationSummary.warningCount} warning
              {validationSummary.warningCount !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}

      <div className="flex gap-1 p-0.5 bg-white/[0.02] rounded-md border border-white/[0.04]">
        <button
          onClick={() => setActiveView('mappings')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
            activeView === 'mappings'
              ? 'bg-purple-500/15 text-purple-300 border border-purple-500/20'
              : 'text-white/40 hover:text-white/60 border border-transparent'
          }`}
        >
          <Zap className="w-3 h-3" />
          Mappings
          {mappings.length > 0 && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-white/[0.06]">
              {mappings.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveView('explorer')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
            activeView === 'explorer'
              ? 'bg-purple-500/15 text-purple-300 border border-purple-500/20'
              : 'text-white/40 hover:text-white/60 border border-transparent'
          }`}
        >
          <Database className="w-3 h-3" />
          Explorer
        </button>
        <button
          onClick={() => setActiveView('preview')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded text-[10px] font-medium transition-colors ${
            activeView === 'preview'
              ? 'bg-purple-500/15 text-purple-300 border border-purple-500/20'
              : 'text-white/40 hover:text-white/60 border border-transparent'
          }`}
        >
          <Eye className="w-3 h-3" />
          Preview
        </button>
      </div>

      {activeView === 'mappings' && (
        <div className="space-y-2">
          {showForm && (
            <MappingForm
              elementId={elementId}
              elementType={elementType}
              editingMapping={editingMapping}
              preSelectedSource={preSelectedSource}
              preSelectedTable={preSelectedTable}
              preSelectedColumn={preSelectedColumn}
              preSelectedDataType={preSelectedDataType}
              preSelectedVirtualField={preSelectedVirtualField}
              onClose={handleFormClose}
              onSaved={handleFormSaved}
            />
          )}

          {mappings.length === 0 && !showForm && (
            <div className="text-center py-4">
              <Zap className="w-5 h-5 text-white/15 mx-auto mb-2" />
              <p className="text-xs text-white/30 mb-3">
                No field mappings yet. Map data from Art Director or School Management to
                this element's properties.
              </p>
            </div>
          )}

          {mappings.map((mapping) => (
            <MappingCard
              key={mapping.id}
              mapping={mapping}
              elementId={elementId}
              elementType={elementType}
              onEdit={handleEdit}
            />
          ))}

          {!showForm && (
            <button
              onClick={handleNewMapping}
              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs text-white/40 bg-white/[0.02] border border-dashed border-white/[0.08] rounded-lg hover:bg-white/[0.04] hover:text-white/60 hover:border-white/[0.12] transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Field Mapping
            </button>
          )}
        </div>
      )}

      {activeView === 'explorer' && (
        <div className="rounded-lg border border-white/[0.06] overflow-hidden h-[300px]">
          <SchemaExplorer
            onColumnSelect={handleColumnSelect}
            selectedSource={preSelectedSource}
            selectedTable={preSelectedTable}
            selectedColumn={preSelectedColumn}
            selectedVirtualFieldKey={preSelectedVirtualField?.field_key ?? null}
          />
        </div>
      )}

      {activeView === 'preview' && (
        <DataPreviewPanel mappings={mappings} />
      )}
    </div>
  );
}
